import { Application, BasePlugin } from '@goodboydigital/astro';

import { App } from '../App';

interface RankConfig
{
    regularRacePoints: Record<number, number>,
    nemesisRacePoints: Record<number, number>,
    rankTitles: Record<string, number>
}

interface RankTitle
{
    points: number,
    title: string,
    tier: number
}

export interface RankStatus
{
    progress: number,
    title: string,
    tier: number,
    points: number,
    nextRankPoints: number,
    nextRankTitle: string,
    nextRankTier: number,
}

export class RankPlugin implements BasePlugin
{
    public app: App;
    public lastPointsEarned = 0;

    constructor(app: Application)
    {
        this.app = app as App;
    }

    public increaseRank(position: number, numPlayers: number): void
    {
        const config = this.getConfig();
        const table = numPlayers === 2 ? config.nemesisRacePoints : config.regularRacePoints;

        const earnedPoints = table[position] ? table[position] : 0;
        const currentPoints = this.app.user.getRankPoints();
        const totalPoints = currentPoints + earnedPoints;

        console.log('[RankPlugin] Increase rank from:', currentPoints, 'to:', totalPoints);

        this.lastPointsEarned = earnedPoints;
        this.app.user.setRankPoints(totalPoints);
    }

    public didRankUp(): boolean
    {
        const currentPoints = this.app.user.getRankPoints();
        const statusPrev = this.getRankStatus(currentPoints - this.lastPointsEarned);
        const statusNext = this.getRankStatus(currentPoints);

        return statusPrev.tier !== statusNext.tier;
    }

    public getRankStatus(rankPoints?: number): RankStatus
    {
        if (rankPoints === undefined) rankPoints = this.app.user.getRankPoints();
        const rankList = this.getRankTitlesList();

        rankList.reverse();

        for (let i = 0; i < rankList.length; i++)
        {
            const rank = rankList[i];
            const nextRank = rankList[i - 1];

            if (rankPoints >= rank.points)
            {
                const range = (nextRank ? nextRank.points : rank.points) - rank.points;
                const diff = rankPoints - rank.points;
                const progress = diff / range;

                return {
                    title: rank.title,
                    tier: rank.tier,
                    progress,
                    points: rankPoints,
                    nextRankPoints: nextRank.points,
                    nextRankTitle: nextRank.title,
                    nextRankTier: nextRank.tier,
                };
            }
        }

        return {
            title: 'bronze',
            tier: 1,
            progress: 0,
            points: rankPoints,
            nextRankPoints: 0,
            nextRankTitle: 'bronze',
            nextRankTier: 1,
        };
    }

    private getConfig(): RankConfig
    {
        return this.app.assets.getData('rank') as RankConfig;
    }

    private getRankTitlesList(): RankTitle[]
    {
        const config = this.getConfig();
        const list: RankTitle[] = [];

        for (const name in config.rankTitles)
        {
            const tierMatch = name.match(/(\d+)/);

            if (!tierMatch || !tierMatch.length) throw new Error(`Invalid tier format: ${name}`);
            const tier = Number(tierMatch[1]);
            const title = name.replace(String(tier), '');
            const points = config.rankTitles[name];

            list.push({ title, tier, points });
        }

        list.sort((a, b) => a.points - b.points);

        return list;
    }
}
